import { useState, useEffect } from 'react';
import '../../tailwindcss/Tailwind.css';
import ingestionService from './api';
import PlanBuilder from './components/PlanBuilder';
import OrchestrationList from './components/OrchestrationList';

const IngestData = ({ sourceId, paths }) => {
  const [userGoal, setUserGoal] = useState('');
  const [context, setContext] = useState(() => {
    if (sourceId || paths?.length) {
      return JSON.stringify({ source_id: sourceId, object_paths: paths || [] }, null, 2);
    }
    return '';
  });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [recentOrchestrations, setRecentOrchestrations] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadRecentOrchestrations();
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await ingestionService.getOrchestrationStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load ingestion stats:', err);
    }
  };

  const loadRecentOrchestrations = async () => {
    try {
      const response = await ingestionService.listOrchestrations(undefined, undefined, undefined, 10, 0);
      setRecentOrchestrations(response.orchestrations || []);
    } catch (err) {
      console.error('Failed to load recent orchestrations:', err);
    }
  };

  const handleCreatePlan = async () => {
    if (!userGoal.trim()) {
      alert('Please enter a goal for the orchestration');
      return;
    }

    setLoading(true);
    setPlan(null);
    setExecutionResult(null);

    try {
      let contextObj;
      if (context.trim()) {
        try {
          contextObj = JSON.parse(context);
        } catch {
          alert('Context must be valid JSON or leave it empty.');
          setLoading(false);
          return;
        }
      }

      const newPlan = await ingestionService.createOrchestrationPlan({
        user_goal: userGoal,
        context: contextObj,
      });
      setPlan(newPlan);
      await loadRecentOrchestrations();
    } catch (err) {
      let errorMessage = err.message || 'Unknown error';
      if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
        errorMessage =
          'The orchestration analysis is taking longer than expected. ' +
          'Please try again or simplify your goal.';
      }
      alert(`Failed to create orchestration plan:\n\n${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePlan = async (approved) => {
    if (!plan) return;
    try {
      await ingestionService.approveOrchestrationPlan(plan.id, approved);
      const updatedPlan = await ingestionService.getOrchestrationPlan(plan.id);
      setPlan(updatedPlan);
      if (approved) {
        alert('Plan approved! You can now execute it.');
      } else {
        alert('Plan cancelled.');
      }
    } catch (err) {
      alert(`Failed to approve plan: ${err.message}`);
    }
  };

  const handleExecutePlan = async () => {
    if (!plan) return;
    setExecuting(true);
    setExecutionResult(null);

    try {
      const result = await ingestionService.executeOrchestrationPlan(plan.id);
      setExecutionResult(result);

      const pollInterval = setInterval(async () => {
        try {
          const updatedPlan = await ingestionService.getOrchestrationPlan(plan.id);
          setPlan(updatedPlan);
          if (
            updatedPlan.status === 'completed' ||
            updatedPlan.status === 'failed' ||
            updatedPlan.status === 'partial_success'
          ) {
            clearInterval(pollInterval);
            setExecuting(false);
          }
        } catch {
          clearInterval(pollInterval);
          setExecuting(false);
        }
      }, 2000);

      // Stop polling after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setExecuting(false);
      }, 300000);
    } catch (err) {
      alert(`Failed to execute plan: ${err.message}`);
      setExecuting(false);
    }
  };

  const handleLoadOrchestration = async (orchestrationId) => {
    try {
      const loadedPlan = await ingestionService.getOrchestrationPlan(orchestrationId);
      setPlan(loadedPlan);
      setUserGoal(loadedPlan.user_goal);
      setExecutionResult(null);
    } catch (err) {
      alert(`Failed to load orchestration: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
       

        {/* Ingestion Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-sm text-gray-500 mb-1">Total Ingestions</p>
            <p className="text-3xl font-bold text-gray-900">
              {stats ? stats.total.toLocaleString() : '—'}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-sm text-gray-500 mb-1">Success Rate</p>
            <p className="text-3xl font-bold text-gray-900">
              {stats && stats.successRate !== null ? `${stats.successRate}%` : '—'}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-sm text-gray-500 mb-1">Avg Duration</p>
            <p className="text-3xl font-bold text-gray-900">
              {stats && stats.avgDurationMs !== null
                ? stats.avgDurationMs >= 60000
                  ? `${(stats.avgDurationMs / 60000).toFixed(1)}m`
                  : `${(stats.avgDurationMs / 1000).toFixed(1)}s`
                : '—'}
            </p>
          </div>
        </div>

        <PlanBuilder
          userGoal={userGoal}
          context={context}
          loading={loading}
          plan={plan}
          executing={executing}
          executionResult={executionResult}
          onGoalChange={setUserGoal}
          onContextChange={setContext}
          onCreatePlan={handleCreatePlan}
          onApprovePlan={handleApprovePlan}
          onExecutePlan={handleExecutePlan}
        />

        <OrchestrationList
          orchestrations={recentOrchestrations}
          onSelect={handleLoadOrchestration}
        />
      </div>
    </div>
  );
};

export default IngestData;
